import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserResponse } from '@careforu-tailody/tailody-core-api';

/**
 * Token storage keys - unified across the application
 */
const TOKEN_KEYS = {
    accessToken: 'token',
    refreshToken: 'refreshToken',
};

export interface AuthTokens {
    accessToken?: string;
    refreshToken?: string;
}

interface UserState {
    user: UserResponse | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

interface UserActions {
    setUser: (user: UserResponse | null) => void;
    setAuthenticated: (isAuthenticated: boolean) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    login: (user: UserResponse, tokens?: AuthTokens) => void;
    logout: () => void;
    clearError: () => void;
    setTokens: (tokens: AuthTokens) => void;
    clearTokens: () => void;
    getAccessToken: () => string | null;
}

type UserStore = UserState & UserActions;

const initialState: UserState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

const getJwtPayload = (token: string): { exp?: number } | null => {
    try {
        const body = token.split('.')[1];
        if (!body) return null;

        const normalized = body
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(body.length + (4 - (body.length % 4 || 4)) % 4, '=');

        const decoded = atob(normalized);
        return JSON.parse(decoded) as { exp?: number };
    } catch {
        return null;
    }
};

const isTokenExpired = (token: string): boolean => {
    const payload = getJwtPayload(token);
    const exp = payload?.exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
        return true;
    }
    return exp * 1000 <= Date.now();
};

const clearAuthStorage = (): void => {
    localStorage.removeItem(TOKEN_KEYS.accessToken);
    localStorage.removeItem(TOKEN_KEYS.refreshToken);
};

export const hasValidAccessToken = (): boolean => {
    const token = localStorage.getItem(TOKEN_KEYS.accessToken);
    if (!token) return false;
    return !isTokenExpired(token);
};

export const hasStoredRefreshToken = (): boolean => {
    return Boolean(localStorage.getItem(TOKEN_KEYS.refreshToken));
};

/**
 * 리프레시 토큰이 유효한지(=세션이 살아있는지).
 * JWT면 exp로 판정, 불투명 토큰(디코드 불가)이면 존재만으로 유효 간주.
 * 액세스 토큰이 만료돼도 이걸로 세션을 살려 재발급 기회를 준다.
 */
export const hasValidRefreshToken = (): boolean => {
    const token = localStorage.getItem(TOKEN_KEYS.refreshToken);
    if (!token) return false;
    const payload = getJwtPayload(token);
    const exp = payload?.exp;
    if (typeof exp === 'number' && Number.isFinite(exp)) {
        return exp * 1000 > Date.now();
    }
    return true;
};

export const useUserStore = create<UserStore>()(
    persist(
        (set) => ({
            ...initialState,

            setUser: (user) => set({ user }),
            setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
            setLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),

            /**
             * Login - save user data and tokens
             */
            login: (user, tokens) => {

                // Save tokens to localStorage if provided
                if (tokens?.accessToken) {
                    localStorage.setItem(TOKEN_KEYS.accessToken, tokens.accessToken);
                }
                if (tokens?.refreshToken) {
                    localStorage.setItem(TOKEN_KEYS.refreshToken, tokens.refreshToken);
                }

                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            },

            /**
             * Logout - clear all user data and tokens
             */
            logout: () => {
                // Clear tokens from localStorage
                clearAuthStorage();
                localStorage.removeItem("tailody-user-storage");
                localStorage.removeItem("tailody-group-storage");

                set(initialState);
            },

            clearError: () => set({ error: null }),

            /**
             * Save tokens to localStorage
             */
            setTokens: (tokens) => {
                if (tokens.accessToken) {
                    localStorage.setItem(TOKEN_KEYS.accessToken, tokens.accessToken);
                }
                if (tokens.refreshToken) {
                    localStorage.setItem(TOKEN_KEYS.refreshToken, tokens.refreshToken);
                }
            },

            /**
             * Clear all tokens from localStorage
             */
            clearTokens: () => {
                clearAuthStorage();
            },

            /**
             * Get current access token
             */
            getAccessToken: () => {
                return localStorage.getItem(TOKEN_KEYS.accessToken);
            },
        }),
        {
            name: 'tailody-user-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
            onRehydrateStorage: () => (state) => {
                const tokenValid = hasValidAccessToken();
                // 리프레시 토큰의 exp까지 검사(만료된 리프레시로 세션을 살리지 않도록)
                const canRefresh = hasValidRefreshToken();

                if (!state) return;
                if (!tokenValid && !canRefresh) {
                    state.user = null;
                    state.isAuthenticated = false;
                    state.isLoading = false;
                    state.error = null;
                    return;
                }
                if (!state.user) {
                    state.isAuthenticated = false;
                }
            },
        }
    )
);

// Selectors
export const selectIsAuthenticated = (state: UserStore) => state.isAuthenticated;
export const selectUser = (state: UserStore) => state.user;

// Token keys export for other modules
export { TOKEN_KEYS };
